/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: category image filter tiles (brand PLP entry points)
import { type ReactElement, useCallback, useMemo } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';
import { cn } from '@/lib/utils';

export interface CategoryImageTile {
    /** Category ID to navigate to on click. */
    categoryId: string;
    label: string;
    sub?: string;
    imageUrl: string;
}

interface CategoryImageFiltersProps {
    tiles: CategoryImageTile[];
}

/**
 * Row of tall image cards that navigate to subcategory pages.
 */
export default function CategoryImageFilters({ tiles }: CategoryImageFiltersProps): ReactElement | null {
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const isPending = navigation.state !== 'idle';

    const activeRefinements = useMemo(() => {
        const searchParams = navigation.location ? navigation.location.search : location.search;
        return new URLSearchParams(searchParams).getAll('refine');
    }, [navigation.location, location.search]);

    const handleTileClick = useCallback(
        (categoryId: string) => {
            const params = new URLSearchParams(location.search);
            const refines = params.getAll('refine');
            const cgid = `cgid=${categoryId}`;
            const isSelected = refines.includes(cgid);
            const nextRefines = isSelected
                ? refines.filter((r) => r !== cgid)
                : [...refines.filter((r) => !r.startsWith('cgid=')), cgid];
            params.delete('refine');
            nextRefines.forEach((r) => params.append('refine', r));
            params.set('offset', '0');
            void navigate({ pathname: location.pathname, search: `?${params.toString()}` });
        },
        [location.search, location.pathname, navigate]
    );

    if (!tiles.length) return null;

    return (
        <div
            className={cn(
                'grid gap-3 mb-6',
                tiles.length === 2 && 'grid-cols-2',
                tiles.length === 3 && 'grid-cols-3',
                tiles.length >= 4 && 'grid-cols-2 sm:grid-cols-4',
                isPending && 'pointer-events-none opacity-60 transition-opacity'
            )}>
            {tiles.map((tile) => {
                const isActive = activeRefinements.includes(`cgid=${tile.categoryId}`);
                return (
                    <button
                        key={tile.categoryId}
                        onClick={() => handleTileClick(tile.categoryId)}
                        className={cn(
                            'group relative overflow-hidden aspect-[3/4] block rounded-sm w-full text-left cursor-pointer',
                            isActive && 'ring-2 ring-white ring-inset'
                        )}
                        aria-pressed={isActive}>
                        <img
                            src={tile.imageUrl}
                            alt={tile.label}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent group-hover:from-black/70 transition-all duration-300" />
                        <div className="absolute bottom-0 left-0 p-4">
                            <p
                                style={{ fontFamily: 'var(--font-display)' }}
                                className="text-xl font-light text-primary-foreground leading-tight">
                                {tile.label}
                            </p>
                            {tile.sub && (
                                <p className="text-[10px] tracking-widest uppercase text-primary-foreground/70 mt-0.5">
                                    {tile.sub}
                                </p>
                            )}
                        </div>
                        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                            <span className="flex items-center justify-center w-8 h-8 bg-background rounded-full">
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#1A1A1A"
                                    strokeWidth="2.5">
                                    <path d="M5 12h14M12 5l7 7-7 7" />
                                </svg>
                            </span>
                        </div>
                    </button>
                );
            })}
        </div>
    );
}
